import {
  PublicClientApplication,
  type Configuration,
  LogLevel,
} from '@azure/msal-browser'

// Vite substitutes a missing GitHub secret with an empty string at build time
// rather than failing the build, so a bad workflow config surfaces here as an
// empty import.meta.env value, not a build error. Left unchecked, that empty
// clientId/redirectUri only shows up later as an MSAL ClientConfigurationError
// at login-click time, with no indication of which env var was actually the
// problem. Fail loudly and by name at module load instead.
const REQUIRED_BUILD_TIME_VARS = ['VITE_ENTRA_CLIENT_ID', 'VITE_ENTRA_REDIRECT_URI'] as const
for (const key of REQUIRED_BUILD_TIME_VARS) {
  if (!import.meta.env[key]) {
    throw new Error(
      `Missing required build-time config: ${key} is empty. The frontend was built without this value -- check that the corresponding GitHub secret is set for the workflow that built this bundle.`
    )
  }
}

const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_ENTRA_CLIENT_ID,
    // /organizations, not a specific tenant ID and not /common: this app is registered
    // multitenant (signInAudience: AzureADMultipleOrgs) so any customer's Entra tenant can
    // sign in, differentiated server-side by the JWT `tid` claim (withAuthPooled.ts). /common
    // would also admit personal Microsoft accounts, which have no tid the tenant registry
    // could ever match.
    authority: 'https://login.microsoftonline.com/organizations',
    redirectUri: import.meta.env.VITE_ENTRA_REDIRECT_URI,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      logLevel: import.meta.env.DEV ? LogLevel.Warning : LogLevel.Error,
      loggerCallback: (level, message) => {
        if (level === LogLevel.Error) console.error('[MSAL]', message)
        else if (import.meta.env.DEV) console.debug('[MSAL]', message)
      },
    },
  },
}

export const msalInstance = new PublicClientApplication(msalConfig)

export const loginRequest = {
  scopes: [`api://${import.meta.env.VITE_ENTRA_CLIENT_ID}/user_impersonation`],
}
